/**
 * Mobile Rate Calculations Hook
 * Extracted from CreateTransactionMobile.tsx
 *
 * Handles currency rate calculations and auto-updates.
 * Uses same backend as desktop: formatRate from transactionUtils, optional calculateTransferRate from useCurrencyHelpers.
 */

import { useEffect, useCallback } from "react";
import { Account, Currency, Transaction } from "../../../../types";
import { formatRate } from "../../../../utils/transactionUtils";

export interface UseMobileRateCalculationsParams {
  transactionType: "expense" | "income" | "transfer";
  formData: {
    accountId: string;
    currency: string;
    rate: string;
    defaultCurrencyRate: string;
  };
  transferFormData?: {
    fromAccountId: string;
    toAccountId: string;
    transferRate: string;
  };
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  rateManuallyEdited: boolean;
  defaultCurrencyRateManuallyEdited: boolean;
  focusedField?: "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;
  isEditMode?: boolean;
  editingTransaction?: Transaction | null;
  setFormData: (updater: (prev: any) => any) => void;
  setTransferFormData?: (updater: (prev: any) => any) => void;
  setRateManuallyEdited: (value: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (value: boolean) => void;
  /** When provided, use this (e.g. from useCurrencyHelpers) so mobile and desktop share same backend */
  calculateTransferRateIn?: (fromCurrency: string, toCurrency: string) => number;
  /** Called with the new rate after refresh so the calculator can be updated when exchange rate field is focused */
  onRateUpdated?: (newRate: string) => void;
  /** Called with the new rate after refresh so the calculator can be updated when default currency rate field is focused */
  onDefaultRateUpdated?: (newRate: string) => void;
}

export interface UseMobileRateCalculationsResult {
  getCurrencyRate: (currencyCode: string) => number;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  handleRefreshRate: () => void;
  handleRefreshDefaultCurrencyRate: () => void;
}

/**
 * Hook for currency rate calculations
 */
export const useMobileRateCalculations = ({
  transactionType,
  formData,
  transferFormData,
  accounts,
  currencies,
  defaultCurrency,
  rateManuallyEdited,
  defaultCurrencyRateManuallyEdited,
  focusedField,
  isEditMode,
  editingTransaction,
  setFormData,
  setTransferFormData,
  setRateManuallyEdited,
  setDefaultCurrencyRateManuallyEdited,
  calculateTransferRateIn,
  onRateUpdated,
  onDefaultRateUpdated,
}: UseMobileRateCalculationsParams): UseMobileRateCalculationsResult => {
  // Helper function to get currency rate from settings (same as useCurrencyHelpers)
  const getCurrencyRate = useCallback(
    (currencyCode: string): number => {
      if (currencyCode === defaultCurrency) return 1;
      const currency = currencies.find((c) => c.code === currencyCode);
      return currency?.rate || 1;
    },
    [currencies, defaultCurrency]
  );

  // Use provided calculateTransferRate (from useCurrencyHelpers) or compute same formula locally
  const calculateTransferRate = useCallback(
    (fromCurrency: string, toCurrency: string): number => {
      if (calculateTransferRateIn) return calculateTransferRateIn(fromCurrency, toCurrency);
      if (fromCurrency === toCurrency) return 1;
      const fromRate = getCurrencyRate(fromCurrency);
      const toRate = getCurrencyRate(toCurrency);
      return toRate / fromRate;
    },
    [getCurrencyRate, calculateTransferRateIn]
  );

  // Handle refresh rate from settings (same as desktop: formatRate from transactionUtils)
  const handleRefreshRate = useCallback(() => {
    if (transactionType === "transfer" && transferFormData && setTransferFormData) {
      // Handle transfer rate refresh
      if (!transferFormData.fromAccountId || !transferFormData.toAccountId) return;

      const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
      const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);
      if (!fromAccount || !toAccount) return;

      const fromCurrency = fromAccount.currency || defaultCurrency;
      const toCurrency = toAccount.currency || defaultCurrency;
      
      if (fromCurrency !== toCurrency) {
        const calculatedRate = calculateTransferRate(fromCurrency, toCurrency);
        const formattedRate = formatRate(calculatedRate);

        setTransferFormData((prev) => ({
          ...prev,
          transferRate: formattedRate,
        }));
        // Reset flag - rate is now from settings
        setRateManuallyEdited(false);
        // Update calculator display if exchange rate field is focused
        onRateUpdated?.(formattedRate);
      }
    } else if (transactionType !== "transfer") {
      // Handle expense/income rate refresh
      if (!formData.accountId || !formData.currency) return;

      const account = accounts.find((acc) => acc.id === formData.accountId);
      if (!account) return;

      const accountCurrency = account.currency || defaultCurrency;
      if (formData.currency !== accountCurrency) {
        const calculatedRate = calculateTransferRate(formData.currency, accountCurrency);
        const formattedRate = formatRate(calculatedRate);

        setFormData((prev) => ({
          ...prev,
          rate: formattedRate,
        }));
        // Reset flag - rate is now from settings (even if it was cleared before)
        setRateManuallyEdited(false);
        // Update calculator display if exchange rate field is focused (avoids stale display from bubbled click)
        onRateUpdated?.(formattedRate);
      }
    }
  }, [transactionType, formData.accountId, formData.currency, transferFormData, accounts, defaultCurrency, calculateTransferRate, setFormData, setTransferFormData, setRateManuallyEdited, onRateUpdated]);

  // Auto-update rate when account or currency changes (like web version)
  useEffect(() => {
    // Skip auto-populate if rate field is currently focused (user is actively editing)
    if (focusedField === "rate") {
      return;
    }
    
    // Handle transfer rate auto-population
    if (transactionType === "transfer" && transferFormData && setTransferFormData) {
      if (transferFormData.fromAccountId && transferFormData.toAccountId) {
        const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
        const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);
        
        if (fromAccount && toAccount) {
          const fromCurrency = fromAccount.currency || defaultCurrency;
          const toCurrency = toAccount.currency || defaultCurrency;
          
          // Only auto-populate if currencies differ
          if (fromCurrency !== toCurrency) {
            // When editing, preserve stored rate if it exists (don't auto-update)
            if (isEditMode && editingTransaction && editingTransaction.type === "transfer") {
              const transactionHasStoredRate = editingTransaction.transferRate !== undefined && editingTransaction.transferRate !== null;
              if (transactionHasStoredRate && transferFormData.transferRate && transferFormData.transferRate !== "") {
                // Preserve the stored rate - don't auto-update
                return;
              }
            }
            
            const calculatedRate = calculateTransferRate(fromCurrency, toCurrency);
            const storedRate = transferFormData.transferRate ? parseFloat(transferFormData.transferRate) : null;
            
            // Check if stored rate is valid and reasonable
            const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
            
            // If rate is empty, invalid, or unreasonable, auto-populate (unless manually edited)
            if (!transferFormData.transferRate || transferFormData.transferRate === "" || !isValidStoredRate) {
              if (!rateManuallyEdited) {
                setTransferFormData((prev) => ({
                  ...prev,
                  transferRate: formatRate(calculatedRate),
                }));
                // Reset flag - rate is from settings
                setRateManuallyEdited(false);
              }
            }
          } else {
            // Same currency, clear rate field
            if (transferFormData.transferRate) {
              setTransferFormData((prev) => ({
                ...prev,
                transferRate: "",
              }));
              setRateManuallyEdited(false);
            }
          }
        }
      }
      return; // Don't process expense/income logic for transfers
    }
    
    // Handle exchange rate (requires accountId)
    if (transactionType !== "transfer" && formData.accountId && formData.currency) {
      const account = accounts.find((acc) => acc.id === formData.accountId);
      if (account) {
        const accountCurrency = account.currency || defaultCurrency;

        // In edit mode never auto-fill/auto-correct rate; preserve transaction rate; user can click "Update rate" (match desktop).
        if (formData.currency !== accountCurrency) {
          const calculatedRate = calculateTransferRate(formData.currency, accountCurrency);
          const storedRate = formData.rate ? parseFloat(formData.rate) : null;
          
          // Check if stored rate is valid and reasonable
          const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
          
          if (!formData.rate || formData.rate === "" || !isValidStoredRate) {
            if (!rateManuallyEdited && !isEditMode) {
              setFormData((prev) => ({
                ...prev,
                rate: formatRate(calculatedRate),
              }));
              // Reset flag - rate is from settings
              setRateManuallyEdited(false);
            }
          } else if (isValidStoredRate && !rateManuallyEdited && !isEditMode) {
            const rateDifference = Math.abs(storedRate - calculatedRate) / Math.max(storedRate, calculatedRate);
            if (rateDifference > 0.01) {
              setFormData((prev) => ({
                ...prev,
                rate: formatRate(calculatedRate),
              }));
            }
          }
        } else {
          if (!isEditMode && formData.rate) {
            setFormData((prev) => ({
              ...prev,
              rate: "",
            }));
            setRateManuallyEdited(false);
          }
        }
      }
    }
    
    // Auto-update default currency rate when currency changes (independent of account selection)
    // Skip auto-populate if defaultCurrencyRate field is currently focused (user is actively editing)
    // NEVER auto-fill if:
    // 1. defaultCurrencyRateManuallyEdited is true (user manually edited)
    // 2. formData.defaultCurrencyRate has any value (user might be typing or has entered a value)
    // 3. focusedField is "defaultCurrencyRate" (user is actively editing)
    const isDefaultRateFieldFocused = focusedField === "defaultCurrencyRate";
    if (transactionType !== "transfer" && 
        formData.currency && 
        formData.currency !== defaultCurrency && 
        !isDefaultRateFieldFocused &&
        !defaultCurrencyRateManuallyEdited &&
        (!formData.defaultCurrencyRate || formData.defaultCurrencyRate.trim() === "")) {
      // In edit mode: never auto-fill default currency rate from settings. Show only the user's saved rate; user can click "Update rate" to load from settings (match desktop).
      if (isEditMode) return;
      
      // Calculate rate from account currency to default currency (not expense currency)
      const account = formData.accountId ? accounts.find((acc) => acc.id === formData.accountId) : null;
      const accountCurrency = account?.currency || defaultCurrency;
      const calculatedAccountToDefaultRate = accountCurrency !== defaultCurrency 
        ? calculateTransferRate(accountCurrency, defaultCurrency)
        : 1;
      
      // Only auto-fill when not editing (create flow); when editing, rate comes from transaction or refresh button only
      if (accountCurrency !== defaultCurrency) {
        const formattedRate = formatRate(calculatedAccountToDefaultRate);
        setFormData((prev) => ({
          ...prev,
          defaultCurrencyRate: formattedRate,
        }));
        setDefaultCurrencyRateManuallyEdited(false);
      }
      // User can use refresh button to update to current rates (edit or create)
    } else if (transactionType !== "transfer" && formData.currency === defaultCurrency) {
      // Same currency as default, clear default currency rate field
      if (formData.defaultCurrencyRate) {
        setFormData((prev) => ({
          ...prev,
          defaultCurrencyRate: "",
        }));
        setDefaultCurrencyRateManuallyEdited(false);
      }
    }
  }, [formData.accountId, formData.currency, formData.rate, formData.defaultCurrencyRate, transactionType, transferFormData, accounts, currencies, defaultCurrency, rateManuallyEdited, defaultCurrencyRateManuallyEdited, focusedField, isEditMode, editingTransaction, calculateTransferRate, setFormData, setTransferFormData, setRateManuallyEdited, setDefaultCurrencyRateManuallyEdited]);

  // When user manually edits rate, also update defaultCurrencyRate to match the rate
  // This handles cases where user wants custom conversion rates (e.g., rate = 1 means 1 expense = 1 default)
  // This effect should run BEFORE the auto-fill effect to prevent overwriting
  useEffect(() => {
    // Only process for expense/income transactions
    if (transactionType === "transfer") return;
    
    // Only update if rate was manually edited
    if (!rateManuallyEdited || !formData.rate) return;
    
    // Skip if defaultCurrencyRate field is currently focused (user is actively editing it)
    if (focusedField === "defaultCurrencyRate") return;
    
    // Skip if we're in edit mode and transaction has a stored defaultCurrencyRate (preserve it)
    if (isEditMode && editingTransaction && editingTransaction.type !== "transfer") {
      const transactionHasStoredRate = editingTransaction.defaultCurrencyRate !== undefined && editingTransaction.defaultCurrencyRate !== null;
      if (transactionHasStoredRate) return;
    }
    
    const parsedRate = parseFloat(formData.rate);
    if (isNaN(parsedRate) || parsedRate <= 0) return;
    
    const account = formData.accountId ? accounts.find((acc) => acc.id === formData.accountId) : null;
    if (!account) return; // Need account to check currency
    
    const accountCurrency = account.currency || defaultCurrency;
    const expenseCurrency = formData.currency || accountCurrency;
    
    // Check if expense currency equals account currency (currencies match)
    const currenciesMatch = expenseCurrency === accountCurrency;
    
    // When currencies match and rate is manually edited, set defaultCurrencyRate to match the rate
    // This ensures expenseToDefaultRate = exchangeRate * accountToDefaultRate = 1 * rate = rate
    // If user wants expenseToDefaultRate = rate, then accountToDefaultRate = rate
    if (currenciesMatch && accountCurrency !== defaultCurrency) {
      const currentDefaultRate = formData.defaultCurrencyRate ? parseFloat(formData.defaultCurrencyRate) : null;
      // Always update if current rate doesn't match the manually edited rate
      // This ensures the manually edited rate takes precedence over auto-filled rates
      if (currentDefaultRate === null || Math.abs(currentDefaultRate - parsedRate) > 0.0001) {
        const calculatedAccountToDefault = calculateTransferRate(accountCurrency, defaultCurrency);
        const rateMatchesSetting = Math.abs(parsedRate - calculatedAccountToDefault) < 0.0001;
        // Update defaultCurrencyRate to match the manually edited rate
        setFormData((prev) => ({
          ...prev,
          defaultCurrencyRate: formatRate(parsedRate),
        }));
        // Only mark as manually edited if rate differs from setting (so refresh button stays disabled when rate matches)
        setDefaultCurrencyRateManuallyEdited(!rateMatchesSetting);
      }
    }
  }, [formData.rate, formData.defaultCurrencyRate, formData.accountId, formData.currency, rateManuallyEdited, focusedField, transactionType, isEditMode, editingTransaction, accounts, defaultCurrency, setFormData, setDefaultCurrencyRateManuallyEdited, calculateTransferRate]);

  // Handle refresh default currency rate from settings (same as desktop: formatRate from transactionUtils)
  const handleRefreshDefaultCurrencyRate = useCallback(() => {
    if (!formData.accountId || !formData.currency) return;

    const account = accounts.find((acc) => acc.id === formData.accountId);
    if (!account) return;
    
    const accountCurrency = account.currency || defaultCurrency;

    if (formData.currency !== defaultCurrency && accountCurrency !== defaultCurrency) {
      // Calculate rate from account currency to default currency (not expense currency)
      const calculatedRate = calculateTransferRate(accountCurrency, defaultCurrency);
      const formattedRate = formatRate(calculatedRate);

      setFormData((prev) => ({
        ...prev,
        defaultCurrencyRate: formattedRate,
      }));
      // Reset flag - rate is now from settings
      setDefaultCurrencyRateManuallyEdited(false);
      // Update calculator display if default currency rate field is focused (avoids stale display from bubbled click)
      onDefaultRateUpdated?.(formattedRate);
    }
  }, [formData.accountId, formData.currency, accounts, defaultCurrency, calculateTransferRate, setFormData, setDefaultCurrencyRateManuallyEdited, onDefaultRateUpdated]);

  return {
    getCurrencyRate,
    calculateTransferRate,
    handleRefreshRate,
    handleRefreshDefaultCurrencyRate,
  };
};
